/**
 * Shared primitives for BrandIdentity and the read-only account branding tab.
 *
 * Exports:
 *   - FileEntry          – typed file entry with optional image dimensions
 *   - HEX_COLOR_RE       – validates 3- or 6-character hex color strings
 *   - SMALL_IMAGE_THRESHOLD_PX – reserved for logo small-dimension warning (UI currently off)
 *   - ColorSwatchAdornment    – MUI InputAdornment showing a live hex color preview
 *   - AttachedFilesList       – file list with thumbnail/icon, metadata, and customisable action slot
 *   - useFileEntries          – hook managing a list of FileEntry items + URL lifecycle (optional resetWhenKey)
 *   - brandingFileDisplayName – label from API originalFilename or filePath tail
 *   - brandingLocalFileDedupeKey – local attach de-duplication (name + size)
 *   - RemoteLogoThumbnail     – server logo preview in a 64×64 tile (inline presigned URL)
 *   - RemoteBrandingFilesList – server-stored branding rows (edit form); optional logo thumbnails
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import FileIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/File.js';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { isImageFile, loadImageDimensions } from '../../utils/fileUtils';
import { formatFileSize } from '../../utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FileEntry = {
  file: File;
  objectUrl: string;
  /** Pixel width — populated asynchronously for image files. */
  width?: number;
  /** Pixel height — populated asynchronously for image files. */
  height?: number;
};

// ─── Constants ────────────────────────────────────────────────────────────────

/** Matches 3- or 6-character hex color strings, e.g. #RGB or #RRGGBB. */
export const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/**
 * Images with either dimension below this value (in pixels) trigger the
 * "too small to display well" warning in the file list.
 */
export const SMALL_IMAGE_THRESHOLD_PX = 150;

/** Display name for a file from GET /accounts/{id}/settings (original name or path tail). */
export function brandingFileDisplayName(filePath: string, originalFilename: string | null | undefined): string {
  const trimmed = originalFilename?.trim();
  if (trimmed) return trimmed;
  const parts = filePath.split(/[/\\]/);
  const last = parts[parts.length - 1];
  return last || filePath;
}

/**
 * Stable key for a picked {@link File} vs other locals and remote branding rows
 * (case-insensitive display name + byte size).
 */
export function brandingLocalFileDedupeKey(file: File): string {
  return `${file.name.trim().toLowerCase()}\0${file.size}`;
}

// ─── RemoteBrandingFilesList (server-backed files on the edit form) ───────────

export type BrandingRemoteFile = {
  id: string;
  filePath: string;
  originalFilename: string | null;
  fileSize: number | null;
  /** Pixel dimensions for logo files in `{width}X{height}` format (e.g. "120X80"). Null for documents or when unknown. */
  logoDimensions: string | null;
};

type RemoteBrandingFilesListProps = {
  files: BrandingRemoteFile[];
  /** document: file icon; logo: thumbnail when `logoPreviewUrlByFileId` has a URL, else file icon */
  variant: 'document' | 'logo';
  /** Presigned GET URLs keyed by branding file id — used only when `variant` is `logo`. */
  logoPreviewUrlByFileId?: Readonly<Record<string, string>>;
  /** Optional trailing actions (e.g. remove — hides row in parent until save + inactiveFileIds). */
  renderActions?: (file: BrandingRemoteFile) => ReactNode;
  /** When true, rows render without an outer border (for a parent files-array wrapper). */
  embedded?: boolean;
  /** Opens a full-size logo preview when a logo thumbnail is clicked. */
  onLogoThumbnailClick?: (payload: BrandingLogoPreviewPayload) => void;
};

/** 64×64 logo preview from a presigned GET URL (`inline`); falls back to file icon if the image fails to load. */
export type BrandingLogoPreviewPayload = {
  url: string;
  name: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
};

export function RemoteLogoThumbnail({
  src,
  alt,
  onClick,
}: {
  src: string;
  alt: string;
  onClick?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Box sx={{ flexShrink: 0, display: 'flex', color: 'text.secondary' }}>
        <FileIcon size="lg" />
      </Box>
    );
  }

  const clickable = Boolean(onClick);

  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      sx={{
        flexShrink: 0,
        width: 64,
        height: 64,
        borderRadius: 1,
        bgcolor: 'action.hover',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        p: 0,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: 'action.selected' } : undefined,
        '&:focus-visible': clickable
          ? { outline: 'none', boxShadow: '0px 0px 0px 3px #0b4cce' }
          : undefined,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

/**
 * Read-only list of files already stored for the org (complements local {@link AttachedFilesList}).
 */
export function RemoteBrandingFilesList({
  files,
  variant,
  logoPreviewUrlByFileId,
  renderActions,
  embedded = false,
  onLogoThumbnailClick,
}: RemoteBrandingFilesListProps) {
  if (files.length === 0) return null;

  const rows = files.map((file, index) => {
        const name = brandingFileDisplayName(file.filePath, file.originalFilename);
        const sizeLabel =
          file.fileSize != null && Number.isFinite(file.fileSize)
            ? formatFileSize(file.fileSize)
            : '—';

        const previewUrl =
          variant === 'logo' ? logoPreviewUrlByFileId?.[file.id] : undefined;

        const dimParts =
          variant === 'logo' && file.logoDimensions
            ? file.logoDimensions.match(/^(\d+)X(\d+)$/i)
            : null;
        const dimWidth = dimParts ? parseInt(dimParts[1], 10) : undefined;
        const dimHeight = dimParts ? parseInt(dimParts[2], 10) : undefined;

        return (
          <Box key={file.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 1.5 }}>
              {variant === 'logo' ? (
                previewUrl ? (
                  <RemoteLogoThumbnail
                    src={previewUrl}
                    alt={name}
                    onClick={
                      onLogoThumbnailClick
                        ? () =>
                            onLogoThumbnailClick({
                              url: previewUrl,
                              name,
                              width: dimWidth,
                              height: dimHeight,
                              sizeBytes: file.fileSize ?? undefined,
                            })
                        : undefined
                    }
                  />
                ) : (
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <FileIcon size="lg" />
                  </Box>
                )
              ) : (
                <Box sx={{ flexShrink: 0, display: 'flex', color: 'text.secondary' }}>
                  <FileIcon size="lg" />
                </Box>
              )}
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>
                  {name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {dimWidth !== undefined && dimWidth > 0 && dimHeight !== undefined && dimHeight > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {dimWidth} × {dimHeight} px
                      </Typography>
                      <Typography variant="caption" color="text.secondary" aria-hidden>
                        &bull;
                      </Typography>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {sizeLabel}
                  </Typography>
                </Stack>
              </Stack>
              {renderActions ? (
                <Box sx={{ flexShrink: 0 }}>{renderActions(file)}</Box>
              ) : null}
            </Box>
            {index < files.length - 1 && <Divider />}
          </Box>
        );
  });

  if (embedded) return <>{rows}</>;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {rows}
    </Box>
  );
}

// ─── ColorSwatchAdornment ─────────────────────────────────────────────────────

/**
 * A circular swatch placed as a MUI start adornment inside color hex input fields.
 * Fills with the entered color when valid; falls back to an outlined empty circle.
 */
function parseHexColorForSwatch(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  const match = withHash.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (!match) return null;
  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  return `#${hex}`;
}

export function ColorSwatchAdornment({
  value,
  /** Figma Spacing/3 — gap between swatch and hex text. */
  gapAfter = 12,
}: {
  value: string;
  gapAfter?: number | string;
}) {
  const swatchColor = parseHexColorForSwatch(value) ?? (HEX_COLOR_RE.test(value) ? value : null);
  const { tokens } = useTheme() as Theme & LensTheme;
  const marginRight = typeof gapAfter === 'number' ? `${gapAfter}px` : gapAfter;
  return (
    <InputAdornment position="start" sx={{ marginRight }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          flexShrink: 0,
          outline: `1.5px solid ${tokens.semantic.color.ui.divider.default.value}`,
          outlineOffset: '1px',
          ...(swatchColor
            ? { bgcolor: swatchColor }
            : { border: 1, borderColor: 'divider', bgcolor: 'background.paper' }),
        }}
      />
    </InputAdornment>
  );
}

// ─── AttachedFilesList ────────────────────────────────────────────────────────

type AttachedFilesListProps = {
  entries: FileEntry[];
  /**
   * Render the action button(s) for each row.
   * Receives the entry and its index so callers can wire delete/download.
   */
  renderActions: (entry: FileEntry, index: number) => ReactNode;
  /** When true, rows render without an outer border (for a parent files-array wrapper). */
  embedded?: boolean;
  /** When embedded, draw a divider above the first local row (e.g. after remote rows). */
  showTopDivider?: boolean;
  /** Show small-logo warning for image rows below SMALL_IMAGE_THRESHOLD_PX (onboarding). */
  showSmallLogoWarning?: boolean;
  smallLogoWarningText?: string;
  /** Opens a full-size logo preview when an image thumbnail is clicked. */
  onLogoThumbnailClick?: (payload: BrandingLogoPreviewPayload) => void;
};

/**
 * Renders a bordered list of attached files.
 * Each row shows a thumbnail (images) or file icon (docs), filename, metadata,
 * and the actions provided via `renderActions`.
 */
export function AttachedFilesList({
  entries,
  renderActions,
  embedded = false,
  showTopDivider = false,
  showSmallLogoWarning = false,
  smallLogoWarningText,
  onLogoThumbnailClick,
}: AttachedFilesListProps) {
  const { tokens } = useTheme() as Theme & LensTheme;
  const warningColor = tokens.semantic.color.status.warning.default.value;

  if (entries.length === 0) return null;

  const rows = entries.map((entry, index) => {
        const { file, objectUrl, width, height } = entry;
        const isImage = isImageFile(file);
        const isTooSmall =
          showSmallLogoWarning &&
          isImage &&
          width !== undefined &&
          height !== undefined &&
          (width < SMALL_IMAGE_THRESHOLD_PX || height < SMALL_IMAGE_THRESHOLD_PX);

        return (
          <Box key={objectUrl}>
            {embedded && showTopDivider && index === 0 && <Divider />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 1.5 }}>
              {/* Leading — thumbnail for images, file icon for documents */}
              {isImage ? (
                <Box
                  component={onLogoThumbnailClick ? 'button' : 'div'}
                  type={onLogoThumbnailClick ? 'button' : undefined}
                  onClick={
                    onLogoThumbnailClick
                      ? () =>
                          onLogoThumbnailClick({
                            url: objectUrl,
                            name: file.name,
                            width,
                            height,
                            sizeBytes: file.size,
                          })
                      : undefined
                  }
                  sx={{
                    flexShrink: 0,
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    p: 0,
                    cursor: onLogoThumbnailClick ? 'pointer' : 'default',
                    '&:hover': onLogoThumbnailClick ? { bgcolor: 'action.selected' } : undefined,
                    '&:focus-visible': onLogoThumbnailClick
                      ? { outline: 'none', boxShadow: '0px 0px 0px 3px #0b4cce' }
                      : undefined,
                  }}
                >
                  <Box
                    component="img"
                    src={objectUrl}
                    alt={file.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ flexShrink: 0, display: 'flex', color: 'text.secondary' }}>
                  <FileIcon size="lg" />
                </Box>
              )}

              {/* Text content */}
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>
                  {file.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {isImage && width !== undefined && height !== undefined && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {width} × {height} px
                      </Typography>
                      <Typography variant="caption" color="text.secondary" aria-hidden>
                        &bull;
                      </Typography>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                </Stack>
                {isTooSmall && smallLogoWarningText ? (
                  <Typography variant="caption" component="p" sx={{ color: warningColor }}>
                    {smallLogoWarningText}
                  </Typography>
                ) : null}
              </Stack>

              {/* Caller-supplied action buttons */}
              <Box sx={{ flexShrink: 0 }}>
                {renderActions(entry, index)}
              </Box>
            </Box>
            {index < entries.length - 1 && <Divider />}
          </Box>
        );
  });

  if (embedded) return <>{rows}</>;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {rows}
    </Box>
  );
}

// ─── useFileEntries ───────────────────────────────────────────────────────────

type UseFileEntriesOptions = {
  /** When true, asynchronously loads pixel dimensions for image files after they are added. */
  loadImages?: boolean;
  /** Maps each picked file to a key for de-duplication against other locals and within the same pick. */
  fileToDedupeKey?: (file: File) => string;
  /** Invoked when one or more picked files were skipped as duplicates of entries already in the list or earlier in the same pick. */
  onDuplicatesSkipped?: (skippedFiles: File[]) => void;
  /** When this string changes, all entries are cleared (e.g. organization switch). */
  resetWhenKey?: string;
};

type UseFileEntriesReturn = {
  entries: FileEntry[];
  /** Pass as the `onChange` handler of a hidden `<input type="file">`. */
  addFiles: (e: React.ChangeEvent<HTMLInputElement>, onAdded?: () => void) => void;
  /** Revokes the object URL and removes the entry at the given index. */
  removeEntry: (index: number) => void;
};

/**
 * Manages a list of attached file entries, including:
 *  - Object URL creation on add
 *  - Asynchronous image dimension loading (opt-in via `loadImages`)
 *  - Object URL revocation on remove and on component unmount
 */
export function useFileEntries({
  loadImages = false,
  fileToDedupeKey = brandingLocalFileDedupeKey,
  onDuplicatesSkipped,
  resetWhenKey,
}: UseFileEntriesOptions = {}): UseFileEntriesReturn {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const entriesRef = useRef<FileEntry[]>(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  useEffect(() => {
    if (resetWhenKey === undefined) return;
    setEntries((prev) => {
      prev.forEach((e) => URL.revokeObjectURL(e.objectUrl));
      return [];
    });
  }, [resetWhenKey]);

  // Revoke all URLs when the component using this hook unmounts (ref holds latest entries).
  useEffect(() => {
    return () => {
      entriesRef.current.forEach((e) => URL.revokeObjectURL(e.objectUrl));
    };
  }, []);

  const addFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, onAdded?: () => void) => {
      if (!e.target.files) return;
      const newFiles = Array.from(e.target.files);
      e.target.value = '';

      const currentKeys = new Set(entriesRef.current.map((entry) => fileToDedupeKey(entry.file)));
      const seenInPick = new Set<string>();
      const accepted: File[] = [];
      const skippedFiles: File[] = [];

      for (const file of newFiles) {
        const k = fileToDedupeKey(file);
        if (currentKeys.has(k) || seenInPick.has(k)) {
          skippedFiles.push(file);
          continue;
        }
        seenInPick.add(k);
        currentKeys.add(k);
        accepted.push(file);
      }

      if (skippedFiles.length > 0) {
        onDuplicatesSkipped?.(skippedFiles);
      }
      if (accepted.length === 0) {
        return;
      }

      const newEntries: FileEntry[] = accepted.map((file) => ({
        file,
        objectUrl: URL.createObjectURL(file),
      }));

      setEntries((prev) => [...prev, ...newEntries]);
      onAdded?.();

      if (loadImages) {
        newEntries.forEach((entry) => {
          if (!isImageFile(entry.file)) return;
          loadImageDimensions(entry.objectUrl)
            .then(({ width, height }) => {
              setEntries((prev) =>
                prev.map((e) =>
                  e.objectUrl === entry.objectUrl ? { ...e, width, height } : e,
                ),
              );
            })
            .catch(() => {/* dimension load failure is non-fatal */});
        });
      }
    },
    [loadImages, fileToDedupeKey, onDuplicatesSkipped],
  );

  const removeEntry = useCallback((index: number) => {
    setEntries((prev) => {
      URL.revokeObjectURL(prev[index].objectUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  return { entries, addFiles, removeEntry };
}
